import { GlobalComposable } from "~/classes/composable/GlobalComposable"

interface State {
  peeking?: StaticFile[]
  isShowingProperties: boolean
  isShowingDiffs: boolean
}
class Peek extends GlobalComposable<State> {
  MAX_FILES = 3
  isPeeking = computed(() => !!this.state.value.peeking)

  override defaultState = (): State => ({
    isShowingProperties: false,
    isShowingDiffs: false,
  })

  override onCreate = () => {
    const { addInterfaceEventListener } = useEvents()
    addInterfaceEventListener("afterHardBlur", this.onHardBlur)
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("afterHardBlur", this.onHardBlur)
  }

  canPeek = (files: StaticFile[]) => {
    if (files.length === 0 || files.length > this.MAX_FILES)
      return false

    return all(files, (file) => {
      if (file.isBlank()) {
        this.error("Asked to peek blank file", file)
        return false
      }
      if (file.isImage())
        return true
      else if (file.isPdf())
        return true
      else if (file.isText())
        return true
      else if (file.isLink())
        return true
      else if (file.isAudio())
        return true
      else if (file.isVideo())
        return true
      else if (file.isDocument())
        return true
      else if (file.isSpreadsheet())
        return true
      else if (file.isPresentation())
        return true
      else
        return false
    })
  }

  canDiff = () => {
    const { selectedFiles } = useSelection()
    return len(selectedFiles.value) === 2
  }

  peekSelectedFiles = () => {
    const { selectedFiles } = useSelection()
    if (!this.canPeek(Object.values(selectedFiles.value)))
      return this.error("Cannot peek selected files")

    // If we cannot show the diffs then we must mark this as false
    if (!this.canDiff())
      this.state.value.isShowingDiffs = false

    this.state.value.peeking = Object.values(selectedFiles.value)
  }

  closePeek = () => {
    this.state.value.peeking = undefined
  }

  peekFiles = (files: StaticFile[], location: SelectionLocation) => {
    if (!this.canPeek(files))
      return this.error("Cannot peek file")

    const { selectFiles } = useSelection()

    selectFiles(files, location)
    this.peekSelectedFiles()
  }

  toggleProperties = () => {
    this.state.value.isShowingProperties = !this.state.value.isShowingProperties
  }

  toggleDiff = () => {
    if (!this.canDiff())
      throw new Error("Cannot toggle diff")
    this.state.value.isShowingDiffs = !this.state.value.isShowingDiffs
    if (this.state.value.isShowingDiffs)
      this.state.value.isShowingProperties = true
  }

  onHardBlur = () => {
    this.closePeek()
  }
}

const peek = new Peek("peek", theme.colors.violet.hex)
export default function () {
  return peek.use()
}
